import { createFileRoute } from "@tanstack/solid-router";
import { For, Show, createSignal } from "solid-js";
import "iconify-icon";
import { PageContainer, PageHeader } from "~/components/layout/page";

// The iconify-icon web component registers itself on import and reads its glyph
// from the icon/width/height attributes, fetching data from the Iconify API on
// first use. It augments HTMLElementTagNameMap but not Solid's JSX, so the tag
// is declared here for the type checker.
declare module "solid-js" {
  namespace JSX {
    interface IntrinsicElements {
      "iconify-icon": HTMLAttributes<HTMLElement> & {
        icon: string;
        width?: string | number;
        height?: string | number;
        mode?: string;
        inline?: boolean;
      };
    }
  }
}

// Hidden icon-set comparison bench, reachable only by typing /dev/icons.
// It renders the app's most-used icons across the current Lucide set and four
// candidate replacements, drawn through the Iconify web component which fetches
// glyph data from the Iconify API at view time.
// The runtime fetch is acceptable here because this is a throwaway chooser; the
// set we land on ships through build-time SVG inlining, so nothing in the real
// app depends on the Iconify API or on @iconify-icon/solid.
export const Route = createFileRoute("/dev/icons")({
  component: IconLab,
});

type SetKey = "lucide" | "ph" | "solar" | "iconoir" | "tabler";

// One row per icon the app uses. lucide/iconoir/tabler hold a full glyph id;
// ph and solar hold a base name that the weight selector suffixes at render.
// A null means the set has no equivalent glyph, and `sub` lists the sets whose
// chosen glyph is only a loose stand-in for the concept.
interface Row {
  name: string;
  use: string;
  lucide: string;
  ph: string;
  solar: string | null;
  iconoir: string | null;
  tabler: string;
  sub?: SetKey[];
}

const ROWS: Row[] = [
  { name: "LayoutDashboard", use: "Nav home", lucide: "layout-dashboard", ph: "squares-four", solar: "widget", iconoir: "view-grid", tabler: "layout-dashboard" },
  { name: "Trophy", use: "Tournaments", lucide: "trophy", ph: "trophy", solar: "cup-star", iconoir: "trophy", tabler: "trophy" },
  { name: "Swords", use: "Duels", lucide: "swords", ph: "sword", solar: null, iconoir: null, tabler: "swords" },
  { name: "Shield", use: "Rankings / admin", lucide: "shield", ph: "shield", solar: "shield", iconoir: "shield", tabler: "shield" },
  { name: "Crown", use: "Champion", lucide: "crown", ph: "crown", solar: "crown", iconoir: "crown", tabler: "crown" },
  { name: "Users", use: "Members", lucide: "users", ph: "users", solar: "users-group-rounded", iconoir: "group", tabler: "users" },
  { name: "Flag", use: "Race / finish", lucide: "flag", ph: "flag", solar: "flag", iconoir: "triangle-flag", tabler: "flag" },
  { name: "Gauge", use: "Class / perf", lucide: "gauge", ph: "gauge", solar: "spedometer-low", iconoir: "dashboard-speed", tabler: "gauge" },
  { name: "Car", use: "Vehicle", lucide: "car", ph: "car", solar: "wheel-angle", iconoir: "car", tabler: "car" },
  { name: "Umbrella", use: "Voucher / rain", lucide: "umbrella", ph: "umbrella", solar: "umbrella", iconoir: "umbrella", tabler: "umbrella" },
  { name: "Target", use: "Hunt target", lucide: "target", ph: "target", solar: "target", iconoir: null, tabler: "target" },
  { name: "Crosshair", use: "Rival aim", lucide: "crosshair", ph: "crosshair", solar: "scanner", iconoir: "scanning", tabler: "crosshair", sub: ["solar", "iconoir"] },
  { name: "History", use: "Ranking history", lucide: "history", ph: "clock-counter-clockwise", solar: "history", iconoir: "clock-rotate-right", tabler: "history" },
  { name: "Gamepad2", use: "Gamertag", lucide: "gamepad-2", ph: "game-controller", solar: "gamepad", iconoir: "gamepad", tabler: "device-gamepad-2" },
  { name: "Plus", use: "Add / create", lucide: "plus", ph: "plus", solar: "add-square", iconoir: "plus", tabler: "plus" },
  { name: "X", use: "Close", lucide: "x", ph: "x", solar: "close-circle", iconoir: "xmark", tabler: "x" },
  { name: "Check", use: "Confirm", lucide: "check", ph: "check", solar: "check-circle", iconoir: "check", tabler: "check" },
  { name: "Search", use: "Search", lucide: "search", ph: "magnifying-glass", solar: "magnifer", iconoir: "search", tabler: "search" },
  { name: "ChevronRight", use: "Nav chevrons", lucide: "chevron-right", ph: "caret-right", solar: "alt-arrow-right", iconoir: "nav-arrow-right", tabler: "chevron-right" },
  { name: "Pencil", use: "Edit", lucide: "pencil", ph: "pencil-simple", solar: "pen", iconoir: "edit-pencil", tabler: "pencil" },
  { name: "Trash2", use: "Delete", lucide: "trash-2", ph: "trash", solar: "trash-bin-trash", iconoir: "trash", tabler: "trash" },
  { name: "Settings", use: "Admin settings", lucide: "settings", ph: "gear-six", solar: "settings", iconoir: "settings", tabler: "settings" },
  { name: "TrendingUp", use: "Rank up", lucide: "trending-up", ph: "trend-up", solar: "graph-up", iconoir: "graph-up", tabler: "trending-up" },
  { name: "ExternalLink", use: "Sheet link", lucide: "external-link", ph: "arrow-square-out", solar: "square-arrow-right-up", iconoir: "open-new-window", tabler: "external-link" },
];

const COLUMNS: { key: SetKey; label: string; current?: boolean }[] = [
  { key: "lucide", label: "Lucide", current: true },
  { key: "ph", label: "Phosphor" },
  { key: "solar", label: "Solar" },
  { key: "iconoir", label: "Iconoir" },
  { key: "tabler", label: "Tabler" },
];

// Phosphor and Solar are multi-weight sets, and the weight is most of what
// separates them from Lucide's single stroke. The selectors swap a suffix that
// every glyph in the set carries.
const PH_WEIGHTS = [
  { label: "Regular", suffix: "" },
  { label: "Bold", suffix: "-bold" },
  { label: "Duotone", suffix: "-duotone" },
  { label: "Fill", suffix: "-fill" },
  { label: "Thin", suffix: "-thin" },
];

const SOLAR_WEIGHTS = [
  { label: "Linear", suffix: "-linear" },
  { label: "Bold", suffix: "-bold" },
  { label: "Outline", suffix: "-outline" },
  { label: "Bold Duotone", suffix: "-bold-duotone" },
  { label: "Broken", suffix: "-broken" },
];

const GRID_STYLE = { "grid-template-columns": "minmax(9rem, 1.1fr) repeat(5, minmax(0, 1fr))" } as const;

function coverage(key: SetKey) {
  let present = 0;
  let subs = 0;
  for (const r of ROWS) {
    const raw = r[key];
    if (raw === null) continue;
    present += 1;
    if (r.sub?.includes(key)) subs += 1;
  }
  return { present, subs, total: ROWS.length };
}

function IconLab() {
  const [size, setSize] = createSignal(28);
  const [ph, setPh] = createSignal(PH_WEIGHTS[0].suffix);
  const [solar, setSolar] = createSignal(SOLAR_WEIGHTS[0].suffix);
  const [onPanel, setOnPanel] = createSignal(false);

  // Resolve a row + column into the full Iconify id, applying the live weight
  // selection for the two multi-weight sets. Null propagates as a coverage gap.
  const idFor = (key: SetKey, row: Row): string | null => {
    switch (key) {
      case "lucide":
        return `lucide:${row.lucide}`;
      case "ph":
        return `ph:${row.ph}${ph()}`;
      case "solar":
        return row.solar ? `solar:${row.solar}${solar()}` : null;
      case "iconoir":
        return row.iconoir ? `iconoir:${row.iconoir}` : null;
      case "tabler":
        return `tabler:${row.tabler}`;
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Icon set bench"
        description="Your 24 most-used icons, in the current Lucide set and four candidates. Change the Phosphor and Solar weights to see how far they move from Lucide's generic stroke."
      />

      <div class="flex flex-wrap items-center gap-x-6 gap-y-3 rounded-lg border border-border-subtle bg-surface-2 px-4 py-3">
        <label class="flex items-center gap-2 text-sm text-text-muted">
          Size
          <input type="range" min="16" max="48" value={size()} onInput={(e) => setSize(+e.currentTarget.value)} />
          <span class="w-8 tabular-nums text-text-base">{size()}</span>
        </label>

        <label class="flex items-center gap-2 text-sm text-text-muted">
          Phosphor weight
          <select
            class="rounded border border-border-subtle bg-surface-3 px-2 py-1 text-text-base"
            value={ph()}
            onChange={(e) => setPh(e.currentTarget.value)}
          >
            <For each={PH_WEIGHTS}>{(w) => <option value={w.suffix}>{w.label}</option>}</For>
          </select>
        </label>

        <label class="flex items-center gap-2 text-sm text-text-muted">
          Solar weight
          <select
            class="rounded border border-border-subtle bg-surface-3 px-2 py-1 text-text-base"
            value={solar()}
            onChange={(e) => setSolar(e.currentTarget.value)}
          >
            <For each={SOLAR_WEIGHTS}>{(w) => <option value={w.suffix}>{w.label}</option>}</For>
          </select>
        </label>

        <label class="flex items-center gap-2 text-sm text-text-muted">
          <input type="checkbox" checked={onPanel()} onChange={(e) => setOnPanel(e.currentTarget.checked)} />
          On card panel
        </label>
      </div>

      <div class="flex flex-wrap gap-2 text-xs text-text-muted">
        <span class="text-text-faint">Coverage of your icons:</span>
        <For each={COLUMNS}>
          {(col) => {
            const c = coverage(col.key);
            const full = c.present === c.total && c.subs === 0;
            return (
              <span
                class="rounded-full border px-2 py-0.5"
                classList={{
                  "border-border-subtle text-text-base": full,
                  "border-amber-500/40 text-amber-400/90": !full,
                }}
              >
                {col.label} {c.present}/{c.total}
                <Show when={c.subs > 0}> · {c.subs} stand-in</Show>
              </span>
            );
          }}
        </For>
        <span class="text-text-faint">— = no equivalent glyph · ≈ = loose stand-in</span>
      </div>

      <div class="overflow-x-auto">
        <div class="min-w-[44rem]">
          {/* Header row */}
          <div class="grid items-end gap-px" style={GRID_STYLE}>
            <div class="px-2 pb-2 text-xs uppercase tracking-widest text-text-faint">Icon</div>
            <For each={COLUMNS}>
              {(col) => (
                <div class="flex flex-col items-center gap-1 px-2 pb-2 text-center">
                  <span class="font-display text-sm font-semibold tracking-wide text-text-base">{col.label}</span>
                  <Show when={col.current}>
                    <span class="rounded-full bg-surface-3 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-text-muted">current</span>
                  </Show>
                </div>
              )}
            </For>
          </div>

          {/* Icon rows */}
          <div class="rounded-lg border border-border-subtle" classList={{ "bg-surface-2": onPanel() }}>
            <For each={ROWS}>
              {(row, i) => (
                <div class="grid items-center gap-px border-t border-border-subtle first:border-t-0" style={GRID_STYLE}>
                  <div class="flex flex-col px-3 py-2">
                    <span class="font-mono text-[13px] text-text-base">{row.name}</span>
                    <span class="text-[11px] text-text-faint">{row.use}</span>
                  </div>
                  <For each={COLUMNS}>
                    {(col) => {
                      const id = idFor(col.key, row);
                      const isSub = !!row.sub?.includes(col.key);
                      return (
                        <div
                          class="relative flex items-center justify-center py-2 text-text-base"
                          classList={{ "bg-surface-1/40": i() % 2 === 1 && !onPanel() }}
                        >
                          <Show
                            when={id}
                            fallback={
                              <span class="flex items-center justify-center text-text-faint" style={{ width: `${size()}px`, height: `${size()}px` }}>
                                —
                              </span>
                            }
                          >
                            <iconify-icon icon={id!} width={size()} height={size()} />
                          </Show>
                          <Show when={isSub}>
                            <span class="absolute right-1 top-1 text-[11px] leading-none text-amber-400/80" title="loose stand-in">
                              ≈
                            </span>
                          </Show>
                        </div>
                      );
                    }}
                  </For>
                </div>
              )}
            </For>
          </div>
        </div>
      </div>
    </PageContainer>
  );
}
